/**
 * Bulk charge fundraising guarantee API
 *
 * Two endpoints:
 *   GET  /api/v2/EventAmbassador/{id}          read, for the review summary
 *   POST /api/v2/EventAmbassador/{id}/charge   charge, no request body
 *
 * The charge endpoint requires a system admin credential and returns 201 on
 * success, 400 with `{success: false, error: [{error, param}]}` on failure.
 */

import constants from '../../../constants';
import { EventAmbassador } from './types';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

/**
 * A corrupt or partially written session must not throw here. The charge call
 * evaluates these headers inside its try block, so a parse error would be
 * swallowed and reported as an unknown outcome, telling the operator to go and
 * check Stripe for a request that was never sent. Falling back to no session
 * instead produces an honest 401.
 */
const getSession = () => {
  const raw = localStorage['session'];
  if (!raw || raw === 'undefined') {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getAuthHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: API_KEY || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

const handleResponse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${errorText}`);
  }
  return res.json();
};

/**
 * Read one ambassador. Used both to build the summary and, after each charge,
 * to verify what actually happened.
 */
export const fetchEventAmbassador = async (id: number): Promise<EventAmbassador> => {
  const res = await fetch(`${API_BASE}/api/v2/EventAmbassador/${id}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  // The API wraps the entity: { success, error, data: {...} }.
  const json = await handleResponse<{ data: EventAmbassador }>(res);
  if (!json?.data) {
    throw new Error(`API ${res.status}: empty response`);
  }
  return json.data;
};

export interface ChargeResult {
  httpStatus: number | null;
  /**
   * The raw wire code, e.g. `Id.campaignStatus`, kept verbatim. Null when the
   * request never produced a parseable response, which is itself meaningful:
   * it means we do not know whether a charge was taken.
   */
  errorCode: string | null;
}

interface ChargeErrorBody {
  success?: boolean;
  error?: { error?: string; param?: string }[] | null;
}

/**
 * Charge one ambassador.
 *
 * This never throws. A thrown error would be indistinguishable at the call site
 * from "we know nothing was charged", and the caller has to be able to tell
 * those apart, so every failure mode comes back as a `ChargeResult` instead.
 *
 * The idempotency key means a double-fired button or a browser level retry
 * replays the cached response rather than taking a second payment. It is not a
 * durable guard: the server side cache is short lived.
 */
export const chargeEventAmbassador = async (
  id: number,
  idempotencyKey: string
): Promise<ChargeResult> => {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/v2/EventAmbassador/${id}/charge`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Idempotency-Key': idempotencyKey
      }
    });
  } catch {
    // The request never completed. The server may still have charged the card.
    return { httpStatus: null, errorCode: null };
  }

  if (res.ok) {
    return { httpStatus: res.status, errorCode: null };
  }

  // A 409 from the idempotency filter has no body at all.
  let body: ChargeErrorBody | null = null;
  try {
    body = (await res.json()) as ChargeErrorBody;
  } catch {
    body = null;
  }

  const errorCode = body?.error?.[0]?.error ?? null;
  return { httpStatus: res.status, errorCode };
};
